/**
 * BundleMoveConfirmationModal
 *
 * Confirmation modal for the drag-and-drop that joins a row to a bundle.
 *
 * `targetBundleLabel` is `null` only when the target bundle's header is off the
 * loaded page AND no `offPageHeaders` entry named it: a member renders while its
 * header sits in the previous cursor window, and nothing on screen knows the
 * name. With such an entry the continuation row carries it and the caller quotes
 * it (SYD-2430). The nameless wording covers the rest — a raw document id must
 * never reach the user (SYD-2323). The prompt carries a `data-testid`, not an
 * `aria-label`: a label on non-interactive text would override the visible
 * question as its accessible name.
 */

import { FC, memo } from 'react';
import { Modal, Typography } from 'antd';

const { Text } = Typography;

type BundleMoveConfirmationModalProps = {
  open: boolean;
  targetBundleLabel: string | null;
  onConfirm: () => void;
  onCancel: () => void;
};

const BundleMoveConfirmationModal: FC<BundleMoveConfirmationModalProps> = ({
  open,
  targetBundleLabel,
  onConfirm,
  onCancel,
}) => {
  return (
    <Modal
      aria-label="artable-bundle-move-confirmation-modal"
      title="Move Row to Bundle"
      open={open}
      onOk={onConfirm}
      onCancel={onCancel}
      okText="Move"
      cancelText="Cancel"
    >
      <Text data-testid="artable-bundle-move-confirmation-prompt">
        {targetBundleLabel === null
          ? 'Move this row into the bundle it was dropped on?'
          : <>Move this row to bundle &ldquo;{targetBundleLabel}&rdquo;?</>}
      </Text>
    </Modal>
  );
};

export default memo(BundleMoveConfirmationModal);
